import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { RecallsModal } from "components/Recalls/components/RecallsModal";
import { useConvertRecallToIntervention, useRecallsQuery } from "components/Recalls/hooks";
import "components/Recalls/Recalls.scss";
import { retrieveOpenRecalls } from "components/Recalls/utils";
import { APPOINTMENT_RECALL_STATUSES, RECALL_ENTITY_STATUSSES } from "models";

import type { RecallEntity } from "models";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

export enum RECALL_TYPE {
  DistrimateRecall = 0,
  RDWRecall,
  PonRecall
}

type RecallsProps = {
  selectOnly: boolean;
  showClosedRecalls: boolean;
  hasRecallsData: APPOINTMENT_RECALL_STATUSES;
  vin?: string;
  licensePlate?: string;
  appointmentId?: number;
  dealerLocationId?: number;
  recallsIdsToDisable?: string[];
  interventions?: AddInterventionPayload[];
  onConfirmSelection?: (recalls: AddInterventionPayload[]) => void;
  onOpenConvertRecallToIntervention?: (data: AddInterventionPayload | null) => void;
};

export const Recalls = ({
  vin,
  dealerLocationId,
  recallsIdsToDisable,
  appointmentId,
  onOpenConvertRecallToIntervention,
  licensePlate,
  onConfirmSelection,
  showClosedRecalls,
  selectOnly,
  interventions,
  hasRecallsData
}: RecallsProps) => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: { distrimateRecalls, rdwRecalls, ponRecalls, hasConvertableRecalls },
    loading
  } = useRecallsQuery({ isModalOpen, appointmentId, ...(vin && { vin }), dealer_location_id: dealerLocationId, ...(licensePlate && { license_plate: licensePlate }) });
  const { convertRecallToIntervention } = useConvertRecallToIntervention(appointmentId || 0);

  const [selectedDistrimateRecalls, setSelectedDistrimateRecalls] = useState<RecallEntity[]>([]);
  const [selectedPONRecalls, setSelectedPONRecalls] = useState<RecallEntity[]>([]);
  const [selectedRDWRecalls, setSelectedRDWRecalls] = useState<RecallEntity[]>([]);

  const isDistrimateOrRDWRecallConverted = (recall: RecallEntity) =>
    recall.status !== RECALL_ENTITY_STATUSSES.Unsettled || Boolean(recallsIdsToDisable?.includes(recall.id));
  const isPONRecallConverted = (recall: RecallEntity) => Boolean(recallsIdsToDisable?.includes(recall.id));

  const toggleIsModalOpen = () => {
    setIsModalOpen(isModalOpen => !isModalOpen);
    setSelectedDistrimateRecalls([]);
    setSelectedPONRecalls([]);
    setSelectedRDWRecalls([]);
  };

  const handleSelectedRecall = () => {
    const interventionsFromDistrimateRecalls = selectedDistrimateRecalls.map(recall => convertRecallToIntervention(recall, RECALL_TYPE.DistrimateRecall));
    const interventionsFromPONRecalls = selectedPONRecalls.map(recall => convertRecallToIntervention(recall, RECALL_TYPE.PonRecall));
    const interventionsFromRDWRecalls = selectedRDWRecalls.map(recall => convertRecallToIntervention(recall, RECALL_TYPE.RDWRecall));

    const allInterventions = [...interventionsFromDistrimateRecalls, ...interventionsFromPONRecalls, ...interventionsFromRDWRecalls];
    onConfirmSelection?.(allInterventions);
    toggleIsModalOpen();
  };

  const handleSelectionChange = (recall: RecallEntity, recallType: RECALL_TYPE) => {
    const map = {
      [RECALL_TYPE.DistrimateRecall]: {
        selected: selectedDistrimateRecalls,
        setSelected: setSelectedDistrimateRecalls
      },
      [RECALL_TYPE.RDWRecall]: {
        selected: selectedRDWRecalls,
        setSelected: setSelectedRDWRecalls
      },
      [RECALL_TYPE.PonRecall]: {
        selected: selectedPONRecalls,
        setSelected: setSelectedPONRecalls
      }
    };

    const { selected, setSelected } = map[recallType];

    const isAlreadySelected = selected.some(r => r.id === recall.id);
    setSelected(curr => (isAlreadySelected ? curr.filter(r => r.id !== recall.id) : [...curr, recall]));
  };

  const handleSelectAllRecalls = (checked: boolean) => {
    if (!checked) {
      setSelectedDistrimateRecalls([]);
      setSelectedPONRecalls([]);
      setSelectedRDWRecalls([]);

      return;
    }

    setSelectedRDWRecalls(curr => {
      const newRecalls = retrieveOpenRecalls(rdwRecalls).filter(
        recall => !curr.some(r => r.id === recall.id) && !interventions?.some(intervention => intervention.rdw_recall_id === recall.id)
      );
      return [...curr, ...newRecalls];
    });

    setSelectedDistrimateRecalls(curr => {
      const newRecalls = retrieveOpenRecalls(distrimateRecalls).filter(
        recall => !curr.some(r => r.id === recall.id) && !interventions?.some(intervention => intervention.recalls_id === recall.id)
      );
      return [...curr, ...newRecalls];
    });

    setSelectedPONRecalls(curr => {
      const newRecalls = ponRecalls.filter(
        recall => !curr.some(r => r.id === recall.id) && !interventions?.some(intervention => intervention.pon_recall_id === recall.id)
      );
      return [...curr, ...newRecalls];
    });
  };

  const handleConvertRecallToIntervention = (recall: RecallEntity, recallType: RECALL_TYPE) => {
    const newIntervention = convertRecallToIntervention(recall, recallType);

    onOpenConvertRecallToIntervention?.(newIntervention);
  };

  return (
    <div className="Recalls">
      {selectOnly && hasConvertableRecalls && (
        <Button color="green" type="button" className="add-recalls left-icon mt-14 ml-0" loading={loading} onClick={toggleIsModalOpen}>
          <Icon className="plus" />
          {t("v8_add_recalls", "Add Recall Intervention")}
        </Button>
      )}

      {!selectOnly && [APPOINTMENT_RECALL_STATUSES.Previous, APPOINTMENT_RECALL_STATUSES.Open].includes(hasRecallsData) && (
        <Button
          className={`Recalls-button ${hasRecallsData === APPOINTMENT_RECALL_STATUSES.Open ? "open-recalls" : ""}`}
          basic
          onClick={toggleIsModalOpen}
          loading={loading}
        >
          {t("v8_recalls", "Recalls")}
        </Button>
      )}

      <RecallsModal
        rdwRecalls={rdwRecalls}
        ponRecalls={ponRecalls}
        selectOnly={selectOnly}
        interventions={interventions}
        isOpen={isModalOpen && !loading}
        showClosedRecalls={showClosedRecalls}
        distrimateRecalls={distrimateRecalls}
        onToggleIsModalOpen={toggleIsModalOpen}
        onSelectionConfirm={handleSelectedRecall}
        onSelectAllRecalls={handleSelectAllRecalls}
        isPONRecallConverted={isPONRecallConverted}
        handleSelectionChange={handleSelectionChange}
        onConvertRecallToIntervention={handleConvertRecallToIntervention}
        isDistrimateOrRDWRecallConverted={isDistrimateOrRDWRecallConverted}
      />
    </div>
  );
};
